import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular';

/**
 * Toast notification service for displaying user-friendly messages
 * Provides consistent toast notifications throughout the application
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastController = inject(ToastController);

  /**
   * Show a success message
   */
  async success(message: string, duration: number = 3000): Promise<void> {
    await this.showToast(message, 'success', duration);
  }

  /**
   * Show an error message
   */
  async error(message: string, duration: number = 5000): Promise<void> {
    await this.showToast(message, 'danger', duration);
  }

  /**
   * Show a warning message
   */
  async warning(message: string, duration: number = 4000): Promise<void> {
    await this.showToast(message, 'warning', duration);
  }

  /**
   * Show an info message
   */
  async info(message: string, duration: number = 3000): Promise<void> {
    await this.showToast(message, 'primary', duration);
  }

  /**
   * Show a generic toast with custom color
   */
  private async showToast(
    message: string,
    color: 'success' | 'danger' | 'warning' | 'primary' | 'secondary',
    duration: number
  ): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration,
      color,
      position: 'top',
      buttons: [
        {
          text: 'OK',
          role: 'cancel'
        }
      ],
      cssClass: 'custom-toast'
    });

    await toast.present();
  }

  /**
   * Show a network error message (offline/online)
   */
  async networkError(isOnline: boolean): Promise<void> {
    if (!isOnline) {
      await this.warning(
        'Keine Internetverbindung. Offlinemodus aktiv. / No internet connection. Offline mode active.'
      );
    } else {
      await this.success('Internetverbindung wiederhergestellt. / Internet connection restored.');
    }
  }

  /**
   * Show an API error message with optional retry action
   */
  async apiError(message?: string, retryCallback?: () => void): Promise<void> {
    const defaultMessage =
      'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut. / An error occurred. Please try again.';

    const toast = await this.toastController.create({
      message: message || defaultMessage,
      duration: 6000,
      color: 'danger',
      position: 'top',
      buttons: retryCallback
        ? [
            {
              text: 'Wiederholen / Retry',
              handler: () => {
                retryCallback();
              }
            },
            {
              text: 'Schließen / Close',
              role: 'cancel'
            }
          ]
        : [
            {
              text: 'OK',
              role: 'cancel'
            }
          ],
      cssClass: 'custom-toast'
    });

    await toast.present();
  }

  /**
   * Show a loading error message
   */
  async loadingError(resourceType: string): Promise<void> {
    const messages: { [key: string]: string } = {
      translation: 'Fehler beim Laden der Übersetzung. / Error loading translation.',
      dictionary: 'Fehler beim Laden des Wörterbuchs. / Error loading dictionary.',
      chapter: 'Fehler beim Laden des Kapitels. / Error loading chapter.',
      verse: 'Fehler beim Laden des Verses. / Error loading verse.',
      'cross-references': 'Fehler beim Laden der Querverweise. / Error loading cross-references.',
      search: 'Fehler bei der Suche. / Search error.'
    };

    const message = messages[resourceType] || 'Fehler beim Laden der Daten. / Error loading data.';
    await this.error(message);
  }

  /**
   * Show an import success message
   */
  async importSuccess(resourceType: string, name: string): Promise<void> {
    await this.success(
      `${resourceType} "${name}" erfolgreich importiert. / ${resourceType} "${name}" imported successfully.`,
      4000
    );
  }

  /**
   * Show an import error message
   */
  async importError(resourceType: string, error?: string): Promise<void> {
    const message = error
      ? `Fehler beim Import: ${error} / Import error: ${error}`
      : `Fehler beim Import von ${resourceType}. / Error importing ${resourceType}.`;
    await this.error(message, 6000);
  }

  /**
   * Show a delete confirmation result
   */
  async deleteSuccess(resourceType: string): Promise<void> {
    await this.success(`${resourceType} erfolgreich gelöscht. / ${resourceType} deleted successfully.`);
  }

  /**
   * Show a sync progress message
   */
  async syncProgress(message: string): Promise<void> {
    await this.info(message, 2000);
  }

  /**
   * Show a sync complete message
   */
  async syncComplete(): Promise<void> {
    await this.success('Synchronisierung abgeschlossen. / Synchronization complete.');
  }

  /**
   * Show a sync error message
   */
  async syncError(error?: string): Promise<void> {
    const message = error
      ? `Synchronisierungsfehler: ${error} / Sync error: ${error}`
      : 'Fehler bei der Synchronisierung. / Synchronization error.';
    await this.error(message);
  }
}
